import dynamic from 'next/dynamic';
import { APP_BASE_PATH } from '../../../shared/appBasePath';

const StyledLinkButton = dynamic(() => import('./FAQ.styles').then(mod => mod.StyledLinkButton));
const StyledUl = dynamic(() => import('./FAQ.styles').then(mod => mod.StyledUl));

type Content = {
  boldText: string;
  text: string;
};

const VehicleQuestions = () =>
  [
    {
      boldText: '',
      text: 'License plate number or vehicle identification number (VIN)'
    },
    {
      boldText: '',
      text: 'Overall condition'
    },
    {
      boldText: '',
      text: 'Mileage'
    },
    {
      boldText: '',
      text: 'Features'
    }
  ] as Content[];

const Benefits = () =>
  [
    {
      boldText: 'Better decision making: ',
      text: 'When you have a clear understanding of your car’s value, you can make better decisions about whether to sell, trade in or keep it. For example, if your car is worth more than you owe on your loan, you may consider selling it and using the funds to pay off the loan or upgrade your vehicle.'
    },
    {
      boldText: 'Negotiating power: ',
      text: 'If you plan to sell or trade-in your car, having a realistic understanding of its value gives you negotiating power with potential buyers or dealerships.'
    },
    {
      boldText: 'Insurance savings: ',
      text: 'Car insurance premiums are often based on the value of the car. By tracking the value of your car, you can ensure that you are not overpaying for coverage.'
    },
    {
      boldText: 'Maintenance planning: ',
      text: 'The value of a car can be affected by its condition and maintenance history. By tracking the value of your car, you can make informed decisions about when to invest in maintenance or repairs to ensure that it retains its value over time.'
    }
  ] as Content[];

const Factors = () =>
  [
    {
      boldText: 'Market demand: ',
      text: 'The demand for specific car models, makes and features can vary over time. Popular models with high demand tend to hold their value better, while less desirable or outdated models may experience quicker depreciation.'
    },
    {
      boldText: 'Age and mileage: ',
      text: 'Generally, cars depreciate as they age and accumulate more mileage. '
    },
    {
      boldText: 'Economic factors: ',
      text: 'Economic conditions, including interest rates, inflation and overall consumer spending power, can impact car values. During times of economic downturns, car values may decline due to reduced purchasing power and decreased demand.'
    },
    {
      boldText: 'Seasonal trends: ',
      text: 'Car values can fluctuate seasonally, with peak demand mainly in the spring or fall, and lowest demand in the winter months.'
    },
    {
      boldText: 'Carvana demand: ',
      text: 'Vehicle values are generated using our Carvana Pricing Algorithm which also looks at recent demand for certain vehicles on the Carvana website. '
    }
  ] as Content[];

const MapBulletPoint = (list: Content[], stringVersion = false) => {
  if (stringVersion) {
    return `${list
      .map(item => `<li key=${item.text}><strong>${item.boldText}</strong>${item.text}</li>`)
      .join('')}`;
  }
  return list.map(item => {
    const { boldText, text } = item;
    return (
      <li key={boldText.length > 0 ? boldText : text}>
        <strong>{item.boldText}</strong>
        {item.text}
      </li>
    );
  });
};

const FormatParagraph = (list: Content[], stringVersion = false) => {
  if (stringVersion) {
    return `<ul>${MapBulletPoint(list, true)}</ul>`;
  }

  return <StyledUl>{MapBulletPoint(list)}</StyledUl>;
};

const handleClick = () => {
  window.location.href = `${APP_BASE_PATH}/get-value/vehicle`;
};

export const FAQData = (stringVersion: boolean = true) => [
  {
    q: 'Can I sign up for Carvana Value Tracker if I do not sell my car to or buy my car from Carvana?',
    a: [
      'Yes! You absolutely can sign up for Value Tracker without selling or buying a car with us. It’s an easy, online process to sign up and 100% free.'
    ]
  },
  {
    q: 'How does Carvana determine my car’s value?',
    a: [
      'We review details such as vehicle features, accident history, mileage, service records, trusted pricing guides, Carvana market conditions  and general market analysis to generate real car values for you. We rely on past information that you provided during your original sign up and then we make adjustments based on what we believe your mileage would be at that time.'
    ]
  },
  {
    q: 'Can I track the value of more than one car?',
    a: [
      'Yes! You can track all the vehicles that you own and we will send you separate reports for each.',
      stringVersion ? (
        `<a href='${APP_BASE_PATH}/get-value/vehicle'>Get the Carvana Value Tracker Report for a Different Car.</a>`
      ) : (
        <StyledLinkButton onClick={handleClick}>
          Get the Carvana Value Tracker Report for a Different Car.
        </StyledLinkButton>
      )
    ]
  },
  {
    q: 'How do I start tracking my car’s value?',
    a: [
      'To start receiving the Value Tracker reports, we will ask a few questions about your vehicle, such as:',
      stringVersion
        ? FormatParagraph(VehicleQuestions(), true)
        : FormatParagraph(VehicleQuestions()),
      'In addition to the answers you’ll provide, the VIN helps us gather specific details about your vehicle from our trusted partners. Based on this information, we will generate a personalized Vehicle Value Report immediately, plus you will start receiving these reports on a cadence decided by you.'
    ]
  },
  {
    q: 'Why is it important to track the value of my car?',
    a: [
      'Our cars are one of our largest assets. Knowing the value of your vehicle gives you the power to make smart decisions about the maintenance and lifecycle of your vehicle. Benefits include:',
      stringVersion ? FormatParagraph(Benefits(), true) : FormatParagraph(Benefits())
    ]
  },
  {
    q: 'How often do car values change?',
    a: [
      'Car values can change frequently. There are several factors that contribute most:',
      stringVersion ? FormatParagraph(Factors(), true) : FormatParagraph(Factors()),
      'Car values are also influenced by vehicle-specific information such as location, condition, trim and added features.'
    ]
  }
];

const getMainEntity = () =>
  FAQData().map(({ q, a }) => {
    const answer = a.map(item => `<p>${item}</p>`).join('');
    return {
      '@type': 'Question',
      name: `${q}`,
      acceptedAnswer: { '@type': 'Answer', text: `${answer}` }
    };
  });

const FAQStructuredData = () => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: getMainEntity()
});

export const SetFAQStructuredData = () => ({ __html: JSON.stringify(FAQStructuredData()) });
